import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ResendStaffInviteResponse,
  RevokeStaffInviteResponse,
  StaffDirectoryFilters,
  StaffDirectoryResponse,
  StaffDirectoryRole,
  StaffDirectoryStatus,
  StaffAccessUpdateInput,
  StaffAccessUpdateResponse,
  StaffStatusChangeRequest,
  StaffStatusChangeResponse,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase J2 — TanStack Query binding for `GET /me/staff`.
 *
 * Tenant scoping flows through the bearer token's `tenant_id` claim
 * server-side; the FE doesn't need to pass anything extra. The BE
 * gates on the `view_staff` permission (new in Phase J2 — default
 * granted to admin / teacher / staff; never to student / parent).
 *
 * The read endpoint joins the most recent {@link StaffInvite} row per
 * user-email and derives `invite_status` at read time, so the FE
 * gets a fully-flat row shape — no extra fetch per row.
 */

export const staffKeys = {
  all: ["staff"] as const,
  list: (filters?: StaffDirectoryFilters) => [...staffKeys.all, "list", filters ?? {}] as const,
};

function buildStaffQuery(filters?: StaffDirectoryFilters): string {
  const search = new URLSearchParams();
  if (!filters) return "";
  if (filters.role) search.set("role", filters.role);
  if (filters.status) search.set("status", filters.status);
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.q !== undefined && filters.q.length > 0) {
    search.set("q", filters.q);
  }
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useStaffRoster(filters?: StaffDirectoryFilters) {
  const qs = buildStaffQuery(filters);
  return useQuery<StaffDirectoryResponse>({
    queryKey: staffKeys.list(filters),
    queryFn: () => api.get<StaffDirectoryResponse>(`/me/staff${qs ? `?${qs}` : ""}`),
    // Same staleTime as parents — the staff roster mutates rarely
    // (invites + role changes are infrequent) so 15s keeps the FE
    // snappy without sending a refetch on every tab switch.
    staleTime: 15_000,
  });
}

export function useUpdateStaffAccess() {
  const qc = useQueryClient();
  return useMutation<
    StaffAccessUpdateResponse,
    Error,
    { userId: string; body: StaffAccessUpdateInput }
  >({
    mutationKey: ["staff", "access", "update"],
    mutationFn: ({ userId, body }) =>
      api.put<StaffAccessUpdateResponse>(`/me/staff/${userId}/access`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

// ── UI lookups ────────────────────────────────────────────────────────────

/**
 * Ordered most → least privileged. The FE uses this for the role
 * filter `<Select>` so the list renders in a stable, expectable
 * order (admins first — the most common search target).
 */
export const STAFF_ROLE_OPTIONS: ReadonlyArray<{
  value: StaffDirectoryRole;
  label: string;
}> = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "teacher", label: "Teacher" },
  { value: "staff", label: "Staff" },
];

export const STAFF_STATUS_OPTIONS: ReadonlyArray<{
  value: StaffDirectoryStatus;
  label: string;
}> = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "pending_verification", label: "Pending verification" },
];

// ── Phase J2.b — invite resend / revoke mutations ────────────────────────

/**
 * Resend a pending staff invite with a fresh token.
 *
 * Reuses the existing endpoint shipped in Phase E1a / C1b that the
 * first-run wizard's "already invited" panel calls. The "first-run"
 * path naming is historical — the underlying service has no wizard
 * coupling and the existing route guard accepts any admin / owner
 * role at any time after first-run is complete.
 *
 * Refuses non-PENDING invites with `409 INVITE_NOT_PENDING`. The FE
 * gates the affordance on `invite_status in {pending, expired}` so
 * the 409 only fires on a race (two admins clicking simultaneously).
 *
 * Invalidates the entire staff query key on success so the new
 * `expires_at` / `email_send_count` re-paint the row immediately.
 */
export function useResendStaffInvite() {
  const qc = useQueryClient();
  return useMutation<ResendStaffInviteResponse, Error, string>({
    mutationKey: ["staff", "invite", "resend"],
    mutationFn: (inviteId) =>
      api.post<ResendStaffInviteResponse>(`/me/first-run/staff-invites/${inviteId}/resend`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

/**
 * Revoke a pending staff invite. Idempotent — re-revoking returns
 * `revoked: false` but does not 4xx.
 *
 * The FE renders this as a destructive `<AlertDialog>` confirmation
 * before firing the mutation, since revocation kills the existing
 * accept link (any pending email already in the staffer's inbox
 * becomes useless). The mutation does NOT delete the pending User
 * row — that's intentional; user-row lifecycle lives in the SA
 * surface to preserve audit context.
 */
export function useRevokeStaffInvite() {
  const qc = useQueryClient();
  return useMutation<RevokeStaffInviteResponse, Error, string>({
    mutationKey: ["staff", "invite", "revoke"],
    mutationFn: (inviteId) =>
      api.post<RevokeStaffInviteResponse>(`/me/first-run/staff-invites/${inviteId}/revoke`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

// ── Phase J2.d — suspend / reactivate mutations ──────────────────────────

/**
 * Argument shape shared by {@link useSuspendStaff} and
 * {@link useReactivateStaff}. The hooks accept a target `user_id`
 * (NOT an `invite_id` — these endpoints flip the User row, not the
 * invite row) and an optional `reason` that lands on the resulting
 * {@link AuditLog} metadata. Empty / whitespace reasons normalise to
 * `null` server-side, so the FE doesn't need to special-case empty
 * textareas.
 */
export interface StaffStatusChangeMutationInput {
  userId: string;
  reason?: string | null;
}

/**
 * Phase J2.d — suspend a staff `User` row.
 *
 * Endpoint: `POST /me/staff/{user_id}/suspend`. BE-gated on the new
 * `manage_staff` permission (admin-only by default; owner/super_admin
 * keep wildcard). The route writes a `staff.suspended` audit row with
 * actor + target + prior/next status + the optional reason.
 *
 * Possible error shapes:
 * - `403 STAFF_SELF_TARGET_FORBIDDEN` — caller targeted their own row
 *   (the FE row dropdown already hides this action, but the BE guard
 *   exists for stale-bundle defence).
 * - `404` — unknown / cross-tenant / soft-deleted / non-staff target.
 * - `409 STAFF_ALREADY_SUSPENDED` — mirror-suspend race (two admins
 *   clicking simultaneously).
 *
 * On success the entire staff query key is invalidated so the row's
 * status pill flips immediately without an optimistic merge. The
 * response carries the full post-flip {@link StaffDirectoryEntry} so
 * future iterations can swap in TanStack `setQueryData` for a snappier
 * UX; we keep the simpler `invalidateQueries` path while the surface
 * is fresh.
 */
export function useSuspendStaff() {
  const qc = useQueryClient();
  return useMutation<StaffStatusChangeResponse, Error, StaffStatusChangeMutationInput>({
    mutationKey: ["staff", "suspend"],
    mutationFn: ({ userId, reason }) => {
      const body: StaffStatusChangeRequest = { reason: reason ?? null };
      return api.post<StaffStatusChangeResponse>(`/me/staff/${userId}/suspend`, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

/**
 * Phase J2.d — reactivate a suspended staff `User` row.
 *
 * Mirror of {@link useSuspendStaff}. Endpoint:
 * `POST /me/staff/{user_id}/reactivate`. Same permission, same shape,
 * inverse direction. The 409 code on the failure path is
 * `STAFF_NOT_SUSPENDED` — surfaces when an admin tries to reactivate
 * an already-active or pending-verification row (the FE hides the
 * action on those rows, but the guard exists for stale-bundle
 * defence). The 403 code is identical to suspend's
 * `STAFF_SELF_TARGET_FORBIDDEN` since the self-target rule is
 * symmetric.
 */
export function useReactivateStaff() {
  const qc = useQueryClient();
  return useMutation<StaffStatusChangeResponse, Error, StaffStatusChangeMutationInput>({
    mutationKey: ["staff", "reactivate"],
    mutationFn: ({ userId, reason }) => {
      const body: StaffStatusChangeRequest = { reason: reason ?? null };
      return api.post<StaffStatusChangeResponse>(`/me/staff/${userId}/reactivate`, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}
